import React from 'react';
import { Platform, Share } from 'react-native';
import envConfig from 'config';

import platform from '@lyearn/core/utils/Platform';
import { TFunction, useTranslation } from '@lyearn/i18n';
import { CopyOutline, Download, ShareOutline } from '@lyearn/icons';
import { ActionMenuItemType } from '@lyearn/molecules';
import { Box } from '@lyearn/ui';
import { useAppConfig } from '@/hooks/useAppConfig';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import config from '@/modules/content/config';
import useFileDownloader from '@/modules/corporate/modules/micro-learning/views/MicroLearningViewer/hooks/useFileDownloader';
import useMicroLearningPublicUrl from '@/modules/corporate/modules/micro-learning/views/MicroLearningViewer/hooks/useMicroLearningPublicUrl';
import { Maybe, PublicAccessConfigType, ResourceTypeEnum } from '@/types/schema';
import Clipboard from '@/utils/general/Clipboard';

import { useBaseCard } from '../Provider';

const getStartIcon = (
  Icon: (
    _props: React.SVGProps<SVGSVGElement> & {
      fillColorToken?: string;
    },
  ) => JSX.Element,
) => (
  <Box className="flex h-24 w-24 items-center justify-center rounded-full me-20">
    <Icon className="h-24 w-24" fillColorToken="icon-primary" />
  </Box>
);

const copyItem = (t: TFunction) => ({
  id: 'Copy',
  label: t('Copy'),
  startIcon: getStartIcon(CopyOutline),
  overrides: {
    // @ts-ignore
    Label: {
      variant: 'body-short-01',
    },
  },
});

const shareItem = (t: TFunction) => ({
  id: 'Share',
  label: t('Share'),
  startIcon: getStartIcon(ShareOutline),
  overrides: {
    // @ts-ignore
    Label: {
      variant: 'body-short-01',
    },
  },
});

const downloadItem = (t: TFunction) => ({
  id: 'Download',
  label: t('Actions.Download'),
  startIcon: getStartIcon(Download),
  overrides: {
    // @ts-ignore
    Label: {
      variant: 'body-short-01',
    },
  },
});

export type Props = {
  onClose: () => void;
  contentId?: string;
  contentName?: string;
  contentType?: ResourceTypeEnum;
  publicAccessConfig?: Maybe<PublicAccessConfigType>;
};

const getPathName = (contentId?: string, contentType?: ResourceTypeEnum) => {
  if (contentId && contentType) {
    const contentConfig = config[contentType];
    const { details } = contentConfig.routes;

    return details(contentId);
  }
};
const useOptions = ({
  onClose,
  contentId,
  contentName,
  contentType,
  publicAccessConfig,
}: Props) => {
  const {
    site,
    ui: { allowMicroLearningPublicSharing = false },
  } = useAppConfig();
  const { onCopy } = useCopyToClipboard();
  const { name, type, options } = useBaseCard();
  const downloadFile = useFileDownloader();
  const { t } = useTranslation('Common');
  const { ensurePublicUrl } = useMicroLearningPublicUrl({
    contentId: contentId ?? '',
    contentName: contentName ?? name ?? undefined,
    publicAccessConfig,
  });
  const pathname = options?.path ?? getPathName(contentId, contentType);
  const url = 'https://' + envConfig.host_domain + pathname;
  const isMicroLearningPublicShareEnabled =
    contentType === ResourceTypeEnum.MicroLearning && allowMicroLearningPublicSharing;

  const DEFAULT_ITEMS: ActionMenuItemType[] = React.useMemo(
    () => [
      {
        type: 'divider',
        classes: { root: '-mx-8 pb-10' },
      },
      shareItem(t) as ActionMenuItemType,
      downloadItem(t) as ActionMenuItemType,
      copyItem(t) as ActionMenuItemType,
    ],
    [t],
  );

  const copyAction = React.useCallback(async () => {
    let shareableUrl = url;

    if (isMicroLearningPublicShareEnabled) {
      const nextPublicUrl = await ensurePublicUrl();

      if (!nextPublicUrl) {
        return;
      }

      shareableUrl = nextPublicUrl;
    }

    if (Platform.OS === 'web') {
      onCopy(shareableUrl);
    } else {
      Clipboard.setString(shareableUrl);
    }

    onClose();
  }, [ensurePublicUrl, isMicroLearningPublicShareEnabled, onClose, onCopy, url]);

  const downloadAction = React.useCallback(() => {
    downloadFile(url, name ?? 'file');
    onClose();
  }, [downloadFile, name, onClose, url]);

  const shareAction = React.useCallback(async () => {
    let shareableUrl = url;

    if (isMicroLearningPublicShareEnabled) {
      const nextPublicUrl = await ensurePublicUrl();

      if (!nextPublicUrl) {
        return;
      }

      shareableUrl = nextPublicUrl;
    }

    if (platform.OS === 'web') {
      if (navigator.share) {
        await navigator
          .share({
            title: type ?? site.name,
            text: shareableUrl,
          })
          .finally(() => onClose());
      }
    } else {
      await Share.share(
        {
          title: type ?? site.name,
          message: shareableUrl,
        },
        {
          // Android only:
          dialogTitle: type + t('From') + site.name,
          // iOS only:
          excludedActivityTypes: [],
        },
      )
        .catch((_) => {})
        .finally(() => onClose());
    }
  }, [ensurePublicUrl, isMicroLearningPublicShareEnabled, onClose, site.name, t, type, url]);

  const createOnAction = React.useCallback(
    (onAction: (actionType: string, item: ActionMenuItemType) => void) =>
      (actionType: string, item: ActionMenuItemType) => {
        switch (actionType) {
          case 'Share': {
            void shareAction();
            break;
          }
          case 'Download': {
            downloadAction();
            break;
          }
          case 'Copy': {
            void copyAction();
            break;
          }
          default: {
            onAction(actionType, item);
          }
        }
      },
    [copyAction, downloadAction, shareAction],
  );

  const addItemsAtIndex = React.useCallback(
    (newItems: ActionMenuItemType[], index: number) => {
      const items = [...DEFAULT_ITEMS];
      items.splice(index, 0, ...newItems);

      return items;
    },
    [DEFAULT_ITEMS],
  );

  return { items: DEFAULT_ITEMS, createOnAction, addItemsAtIndex };
};

export default useOptions;
